import { ProductCard } from "./ProductCard";
import { ProductPlaceholderCard } from "./ProductPlaceholderCard";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import type { Product } from "@/lib/catalog";
import type { ReactNode } from "react";

/**
 * Grille produit responsive avec fallback skeleton.
 * Affiche les produits réels quand ils existent, sinon un nombre de placeholders.
 */
export function ProductGrid({
  products,
  placeholderCount = 8,
  title,
  subtitle,
  eyebrow,
  emptyMessage,
  children,
}: {
  products?: Product[];
  placeholderCount?: number;
  title?: ReactNode;
  subtitle?: string;
  eyebrow?: string;
  emptyMessage?: ReactNode;
  children?: ReactNode;
}) {
  const items = products ?? [];
  const hasProducts = items.length > 0;

  return (
    <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:px-10 lg:py-28">
      {title ? (
        <Reveal>
          <SectionHeading eyebrow={eyebrow} title={title} subtitle={subtitle} />
        </Reveal>
      ) : null}

      <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 lg:mt-12 lg:grid-cols-4 lg:gap-6">
        {hasProducts ? (
          items.map((product, i) => (
            <Reveal key={product.id} delay={i * 40}>
              <ProductCard product={product} />
            </Reveal>
          ))
        ) : (
          Array.from({ length: placeholderCount }).map((_, i) => (
            <Reveal key={i} delay={i * 40}>
              <ProductPlaceholderCard />
            </Reveal>
          ))
        )}
      </div>

      {emptyMessage && !hasProducts ? (
        <div className="mt-16 flex flex-col items-center text-center sm:mt-20">
          {emptyMessage}
        </div>
      ) : null}

      {children ? <div className="mt-16 sm:mt-20">{children}</div> : null}
    </section>
  );
}
